
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp,
  Award,
  Clock,
  BookOpen,
  BarChart3,
  CheckCircle2,
  XCircle,
  Brain
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { CurrentAffairsQuestion } from '@/hooks/useCurrentAffairsAdaptive';
import CurrentAffairsAdaptiveNotes from './CurrentAffairsAdaptiveNotes';
import QuestionReviewCard from './QuestionReviewCard';

interface QuizResults {
  score: number;
  totalAnswered: number;
  percentage: number;
  difficultyBreakdown: Record<string, { total: number, correct: number }>;
  dateInfo: { date: string, timePeriod: string | null };
  sessionId: string;
  adaptiveDifficulty: string;
  averageAnswerTime: number;
  timeSpent: { minutes: number, seconds: number };
  allQuestions?: CurrentAffairsQuestion[]; // Added to support all questions
  allAnswers?: (number | null)[]; // Added to support all answers
}

interface CurrentAffairQuizResultProps {
  results: QuizResults;
  timeSpent: { minutes: number, seconds: number };
  questions: CurrentAffairsQuestion[];
  answers: (number | null)[];
  difficulty: string;
}

export const CurrentAffairQuizResult: React.FC<CurrentAffairQuizResultProps> = ({
  results,
  timeSpent,
  questions,
  answers,
  difficulty
}) => {
  const [activeTab, setActiveTab] = useState("summary");
  const navigate = useNavigate();
  
  // Calculate the grade based on percentage
  const getGrade = (percentage: number) => {
    if (percentage >= 90) return { grade: 'A', color: 'text-green-600' };
    if (percentage >= 80) return { grade: 'B', color: 'text-emerald-600' };
    if (percentage >= 70) return { grade: 'C', color: 'text-blue-600' };
    if (percentage >= 60) return { grade: 'D', color: 'text-amber-600' };
    return { grade: 'F', color: 'text-red-600' };
  };
  
  const { grade, color } = getGrade(results.percentage);
  
  // Format time nicely
  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)} seconds`;
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  };
  
  // Get topics where user performed poorly
  const weakTopics = Object.entries(results.difficultyBreakdown)
    .filter(([_, stats]) => (stats.correct / stats.total) < 0.7) // Less than 70% correct
    .map(([topic]) => topic);
    
  // Use the questions and answers from results if available (all questions)
  const reviewQuestions = results.allQuestions || questions;
  const reviewAnswers = results.allAnswers || answers;
  
  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 pb-4">
          <CardTitle className="text-2xl font-bold text-center">
            Quiz Results
          </CardTitle>
          <div className="flex flex-col items-center">
            <div className="w-24 h-24 rounded-full bg-white border-4 border-primary flex items-center justify-center mb-2">
              <span className={`text-3xl font-bold ${color}`}>{grade}</span>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold">
                {results.score} / {results.totalAnswered}
              </div>
              <div className="text-sm text-muted-foreground">
                {results.percentage}% Accuracy
              </div>
            </div>
          </div>
        </CardHeader>
        
        <Tabs defaultValue="summary" value={activeTab} onValueChange={setActiveTab}>
          <div className="px-4 py-2 border-b">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="summary">Summary</TabsTrigger>
              <TabsTrigger value="review">Review Questions ({reviewQuestions.length})</TabsTrigger>
              <TabsTrigger value="adapt">Adaptive Notes</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="summary" className="m-0">
            <CardContent className="p-4">
              <div className="grid gap-4">
                <div className="flex flex-col space-y-1.5">
                  <h3 className="text-sm font-medium">Performance</h3>
                  <Progress value={results.percentage} className="h-2" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Needs Improvement</span>
                    <span>Excellent</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 p-3 border rounded-md">
                    <Clock className="h-5 w-5 text-blue-500" />
                    <div className="space-y-0.5">
                      <p className="text-xs text-muted-foreground">Time Spent</p>
                      <p className="text-sm font-medium">
                        {timeSpent.minutes}m {timeSpent.seconds}s
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 p-3 border rounded-md">
                    <BarChart3 className="h-5 w-5 text-purple-500" />
                    <div className="space-y-0.5">
                      <p className="text-xs text-muted-foreground">Avg. Answer Time</p>
                      <p className="text-sm font-medium">{formatTime(results.averageAnswerTime)}</p>
                    </div>
                  </div>
                </div>
                
                {/* Difficulty breakdown */}
                <div className="border rounded-md p-3">
                  <h3 className="text-sm font-medium mb-2 flex items-center">
                    <TrendingUp className="h-4 w-4 mr-1 text-primary" />
                    Performance by Topic
                  </h3>
                  
                  <div className="space-y-3">
                    {Object.entries(results.difficultyBreakdown).map(([topic, { total, correct }]) => {
                      const percent = total > 0 ? (correct / total) * 100 : 0;
                      let colorClass;
                      if (percent >= 80) colorClass = 'bg-green-500';
                      else if (percent >= 60) colorClass = 'bg-amber-500';
                      else colorClass = 'bg-red-500';
                      
                      return (
                        <div key={topic} className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="font-medium">{topic}</span>
                            <span>{correct}/{total} correct</span>
                          </div>
                          <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${colorClass}`} 
                              style={{ width: `${percent}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Information about the quiz */}
                <div className="border rounded-md p-3">
                  <h3 className="text-sm font-medium mb-2">Quiz Details</h3>
                  <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date:</span>
                      <span className="font-medium">{results.dateInfo.date}</span>
                    </div>
                    {results.dateInfo.timePeriod && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Period:</span>
                        <span className="font-medium">{results.dateInfo.timePeriod}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Questions:</span>
                      <span className="font-medium">{reviewQuestions.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Difficulty:</span>
                      <Badge variant="outline" className="font-medium capitalize">
                        {difficulty}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
            
            <CardFooter className="px-4 pt-0 pb-6">
              <Button 
                variant="outline"
                onClick={() => setActiveTab("adapt")}
                className="w-full"
              >
                <Brain className="mr-2 h-4 w-4" />
                Generate Adaptive Notes
              </Button>
            </CardFooter>
          </TabsContent>
          
          <TabsContent value="review" className="m-0">
            <CardContent className="p-4">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Question Review</h3>
                  <div className="flex gap-2">
                    <div className="flex items-center gap-1 text-xs">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      <span>Correct</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <XCircle className="h-3 w-3 text-red-500" />
                      <span>Incorrect</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {reviewQuestions.map((question, index) => {
                    const userAnswer = reviewAnswers[index];
                    const isCorrect = userAnswer === question.correctAnswer;
                    
                    return (
                      <QuestionReviewCard
                        key={index}
                        question={question}
                        userAnswerIndex={userAnswer}
                        isCorrect={isCorrect}
                        questionNumber={index + 1}
                      />
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </TabsContent>
          
          <TabsContent value="adapt" className="m-0">
            <CardContent className="p-4">
              <CurrentAffairsAdaptiveNotes 
                quizSessionId={results.sessionId}
                difficulty={difficulty as 'easy' | 'medium' | 'hard'}
                topic={weakTopics.length > 0 ? weakTopics.join(', ') : 'Current Affairs'}
                date={results.dateInfo.date}
              />
            </CardContent>
          </TabsContent>
        </Tabs>
        
        <CardFooter className="bg-gray-50 px-4 py-3">
          <div className="w-full flex flex-col sm:flex-row gap-2 justify-between">
            <Button 
              variant="outline" 
              onClick={() => navigate('/current-affairs')}
              size="sm"
            >
              Back to Current Affairs
            </Button>
            
            <div className="flex gap-2">
              <Button 
                variant="outline"
                size="sm"
                onClick={() => navigate('/current-affairs/quiz')}
              >
                Try Another Quiz
              </Button>
              
              <Button 
                onClick={() => navigate('/current-affairs/adaptive-quiz')}
                size="sm"
              >
                <Award className="mr-2 h-4 w-4" />
                Adaptive Quiz
              </Button>
            </div>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default CurrentAffairQuizResult;
